import { z } from 'zod';
/**
 * SCRUM-312 (S2, epic SCRUM-310) — shared "System Name" display-name schema for
 * ISA (`organization`) and MSA (`organization_group`).
 *
 * Every name-bearing boundary (register, create-organization, create/rename
 * group, the child-org saga) MUST validate names through this single schema so
 * the input contract is identical everywhere and the downstream `SystemName`
 * normalization VO never receives an empty / whitespace-only / control-char
 * string (which would otherwise surface as an unmapped 500 rather than a clean
 * 4xx). Rules (validated in order):
 *   1. `.trim()` — strip leading/trailing whitespace BEFORE the length check, so
 *      `'   '` becomes `''` and fails `.min(1)`, while `'  Acme  '` → `'Acme'`.
 *   2. `.min(1)` — non-empty after trim.
 *   3. `.max(maxLength)` — per-context display cap (org/group differ historically).
 *   4. `.refine(...)` — reject ASCII control characters (C0 range U+0000–U+001F
 *      plus DEL U+007F): null bytes, CR/LF, vertical tab, form feed, etc. that
 *      have no place in a display string and enable log-injection / terminal
 *      escapes.
 *
 * The control-char rejection is a `charCodeAt` scan, NOT a regex literal:
 * ESLint's `no-control-regex` forbids regex referencing U+0000–U+001F / U+007F,
 * and project policy forbids inline lint suppressions. Behaviourally equivalent
 * to the negated class `[^\u0000-\u001F\u007F]+` over the trimmed, non-empty
 * string. (Extracted from the original `organization-group.ts` groupNameSchema.)
 */
export function systemDisplayNameSchema(maxLength) {
    return z
        .string()
        .trim()
        .min(1)
        .max(maxLength)
        .refine((s) => {
        for (let i = 0; i < s.length; i += 1) {
            const c = s.charCodeAt(i);
            if (c <= 0x1f || c === 0x7f)
                return false;
        }
        return true;
    }, { message: 'Name must not contain control characters.' });
}
//# sourceMappingURL=system-display-name.js.map